import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  AlertTriangle, Bell, CheckCircle, ChevronRight, Clock, Droplets,
  Eye, FileText, Leaf, MapPin, MessageCircle, Phone, Shield, ShieldAlert,
  Sprout, Target, Thermometer, Tractor, TrendingUp, Users, Zap,
  Bug, Send, X, ChevronDown, BarChart3, FlaskConical, Download,
  Calendar, Star, CircleAlert, CircleDot, Building2, Camera, Receipt,
  Columns3, Euro, Printer, Image
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Couleurs par severite
const SEVERITE_COLORS = {
  critique: { bg: 'bg-red-500/15', border: 'border-red-500/40', text: 'text-red-400', badge: 'bg-red-500', dot: 'bg-red-500' },
  haute: { bg: 'bg-orange-500/15', border: 'border-orange-500/40', text: 'text-orange-400', badge: 'bg-orange-500', dot: 'bg-orange-500' },
  moyenne: { bg: 'bg-amber-500/15', border: 'border-amber-500/40', text: 'text-amber-400', badge: 'bg-amber-500', dot: 'bg-amber-400' },
  basse: { bg: 'bg-blue-500/15', border: 'border-blue-500/40', text: 'text-blue-400', badge: 'bg-blue-500', dot: 'bg-blue-400' },
};

const CERTIF_BADGES = {
  bio: { label: 'BIO', color: 'bg-green-600 text-white' },
  hve: { label: 'HVE', color: 'bg-emerald-600 text-white' },
  conventionnel: { label: 'CONV', color: 'bg-slate-600 text-white' },
};

// Icone pour les types d'alertes
const ALERTE_ICONS = {
  produit_interdit: ShieldAlert,
  risque_controle: Shield,
  dose_incorrecte: AlertTriangle,
  meteo: Thermometer,
  conformite: CircleAlert,
};

export default function ConseillerDashboardPage() {
  const { user, token } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);
  const [showReponseModal, setShowReponseModal] = useState(false);
  const [reponseText, setReponseText] = useState('');
  const [selectedDemande, setSelectedDemande] = useState(null);
  const [pipelineData, setPipelineData] = useState(null);
  const [factures, setFactures] = useState({ factures: [], stats: {} });
  const [diagnostics, setDiagnostics] = useState([]);
  const [fiches, setFiches] = useState([]);

  const headers = useCallback(() => {
    const h = { 'Content-Type': 'application/json' };
    if (token) h['Authorization'] = `Bearer ${token}`;
    const sid = localStorage.getItem('activeSocieteId');
    // Use activeSocieteId, or fallback to user's exploitation_id
    const societeId = sid || user?.exploitation_id || '';
    if (societeId) h['X-Societe-Id'] = societeId;
    return h;
  }, [token, user]);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/conseiller/dashboard`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      }
    } catch (e) {
      console.error('Erreur dashboard:', e);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  const fetchClientDetail = useCallback(async (clientId) => {
    try {
      const res = await fetch(`${API_URL}/api/conseiller/clients/${clientId}`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setClientDetail(data);
      }
    } catch (e) {
      console.error('Erreur detail client:', e);
    }
  }, [headers]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const fetchPipeline = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/conseiller/crm/pipeline`, { headers: headers() });
      if (res.ok) setPipelineData(await res.json());
    } catch (e) { console.error(e); }
  }, [headers]);

  const fetchFactures = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/conseiller/factures`, { headers: headers() });
      if (res.ok) setFactures(await res.json());
    } catch (e) { console.error(e); }
  }, [headers]);

  const fetchDiagnostics = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/conseiller/diagnostics`, { headers: headers() });
      if (res.ok) { const d = await res.json(); setDiagnostics(d.diagnostics || []); }
    } catch (e) { console.error(e); }
  }, [headers]);

  const fetchFiches = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/conseiller/fiches-intervention`, { headers: headers() });
      if (res.ok) { const d = await res.json(); setFiches(d.fiches || []); }
    } catch (e) { console.error(e); }
  }, [headers]);

  useEffect(() => {
    if (activeTab === 'pipeline') fetchPipeline();
    if (activeTab === 'facturation') fetchFactures();
    if (activeTab === 'diagnostic') fetchDiagnostics();
    if (activeTab === 'fiches') fetchFiches();
  }, [activeTab, fetchPipeline, fetchFactures, fetchDiagnostics, fetchFiches]);

  const traiterAlerte = async (alerteId) => {
    try {
      await fetch(`${API_URL}/api/conseiller/alertes/${alerteId}/traiter`, {
        method: 'PUT', headers: headers()
      });
      toast.success('Alerte traitee');
      fetchDashboard();
    } catch (e) { toast.error('Erreur'); }
  };

  const repondreDemande = async () => {
    if (!selectedDemande || !reponseText) return;
    try {
      await fetch(`${API_URL}/api/conseiller/demandes/${selectedDemande.id}/repondre`, {
        method: 'PUT', headers: headers(),
        body: JSON.stringify({ reponse: reponseText, statut: 'traite' })
      });
      toast.success('Reponse envoyee');
      setShowReponseModal(false);
      setReponseText('');
      fetchDashboard();
    } catch (e) { toast.error('Erreur'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0f1a]" data-testid="conseiller-loading">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-400 text-lg">Chargement du tableau de bord conseiller...</p>
        </div>
      </div>
    );
  }

  const stats = dashboard?.stats || {};
  const clients = dashboard?.clients || [];
  const alertes = dashboard?.alertes || [];
  const demandes = dashboard?.demandes || [];
  const interventions = dashboard?.interventions_recentes || [];
  const messages = dashboard?.messages_intelligents || [];
  const alertesCritiques = alertes.filter(a => a.severite === 'critique');

  return (
    <div className="min-h-screen bg-[#0a0f1a] text-white" data-testid="conseiller-dashboard">
      {/* Header */}
      <div className="border-b border-slate-800 bg-[#0d1321]/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center font-bold text-lg">C3</div>
            <div>
              <h1 className="text-lg font-bold text-white">C3 Viti — Espace Conseiller</h1>
              <p className="text-xs text-slate-400">Christophe CHOUVET — Agrement 8400020</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {alertesCritiques.length > 0 && (
              <div className="relative" data-testid="critical-alert-badge">
                <Bell className="w-6 h-6 text-red-400 animate-pulse" />
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-[10px] flex items-center justify-center font-bold animate-bounce">
                  {alertesCritiques.length}
                </span>
              </div>
            )}
            <Badge variant="outline" className="border-emerald-500/40 text-emerald-400 text-xs">
              {stats.nb_clients} clients
            </Badge>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-4 py-4 space-y-4">
        {/* Alertes critiques banniere flash */}
        {alertesCritiques.length > 0 && (
          <div className="rounded-xl border-2 border-red-500/50 bg-red-500/10 p-4 animate-pulse" data-testid="critical-alerts-banner">
            <div className="flex items-center gap-2 mb-2">
              <ShieldAlert className="w-5 h-5 text-red-400" />
              <span className="font-bold text-red-400">{alertesCritiques.length} ALERTE(S) CRITIQUE(S)</span>
            </div>
            <div className="space-y-2">
              {alertesCritiques.map((a) => (
                <div key={a.id} className="flex items-center justify-between bg-red-500/10 rounded-lg px-3 py-2">
                  <div>
                    <span className="text-sm font-semibold text-red-300">{a.client_nom}</span>
                    <span className="text-sm text-red-200/80 ml-2">{a.titre}</span>
                  </div>
                  <Button size="sm" variant="outline" className="border-red-500/40 text-red-400 hover:bg-red-500/20 text-xs"
                    onClick={() => traiterAlerte(a.id)} data-testid={`traiter-alerte-${a.id}`}>
                    Traiter
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stats rapides */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="stats-grid">
          <StatCard icon={Users} label="Clients" value={stats.nb_clients} color="emerald" />
          <StatCard icon={MapPin} label="Hectares" value={`${stats.surface_totale_ha} ha`} color="blue" />
          <StatCard icon={ShieldAlert} label="Alertes critiques" value={stats.alertes_critiques} color="red" flash={stats.alertes_critiques > 0} />
          <StatCard icon={Bell} label="Alertes total" value={stats.alertes_total} color="orange" />
          <StatCard icon={MessageCircle} label="Demandes" value={stats.demandes_en_attente} color="violet" />
          <StatCard icon={TrendingUp} label="Conformite moy." value={`${stats.conformite_moyenne}%`} color={stats.conformite_moyenne >= 80 ? 'emerald' : 'amber'} />
        </div>

        {/* Navigation tabs */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {[
            { key: 'overview', label: 'Vue globale', icon: BarChart3 },
            { key: 'pipeline', label: 'CRM Pipeline', icon: Columns3 },
            { key: 'facturation', label: 'Facturation', icon: Receipt },
            { key: 'diagnostic', label: 'Diagnostic Photo', icon: Camera },
            { key: 'fiches', label: 'Fiches Intervention', icon: Printer },
            { key: 'alertes', label: `Alertes (${alertes.length})`, icon: Bell },
            { key: 'demandes', label: `Demandes (${demandes.length})`, icon: MessageCircle },
            { key: 'clients', label: 'Clients', icon: Users },
            { key: 'interventions', label: 'Interventions', icon: Tractor },
            { key: 'casia', label: 'Produits CASIA', icon: FlaskConical },
          ].map(tab => (
            <button key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === tab.key
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
              data-testid={`tab-${tab.key}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Contenu principal */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Messages intelligents */}
            <div className="lg:col-span-2 space-y-3">
              <h2 className="text-sm font-bold text-slate-300 uppercase tracking-wider">Messages intelligents</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {messages.map((msg) => (
                  <SmartMessageCard key={msg.id} message={msg} />
                ))}
              </div>

              {/* Dernieres interventions */}
              <h2 className="text-sm font-bold text-slate-300 uppercase tracking-wider mt-4">Dernieres interventions</h2>
              <div className="space-y-2">
                {interventions.slice(0, 5).map((interv) => (
                  <InterventionRow key={interv.id} intervention={interv} />
                ))}
              </div>
            </div>

            {/* Sidebar: Demandes + Alertes recentes */}
            <div className="space-y-3">
              <h2 className="text-sm font-bold text-slate-300 uppercase tracking-wider">Demandes clients</h2>
              <div className="space-y-2">
                {demandes.slice(0, 3).map((dem) => (
                  <DemandeCard key={dem.id} demande={dem}
                    onRepondre={() => { setSelectedDemande(dem); setShowReponseModal(true); }} />
                ))}
                {demandes.length === 0 && <p className="text-sm text-slate-500">Aucune demande en attente</p>}
              </div>

              <h2 className="text-sm font-bold text-slate-300 uppercase tracking-wider mt-4">Alertes recentes</h2>
              <div className="space-y-2">
                {alertes.slice(0, 4).map((a) => (
                  <AlerteCard key={a.id} alerte={a} onTraiter={() => traiterAlerte(a.id)} />
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'alertes' && (
          <div className="space-y-3" data-testid="alertes-tab">
            {alertes.map((a) => (
              <AlerteCard key={a.id} alerte={a} expanded onTraiter={() => traiterAlerte(a.id)} />
            ))}
            {alertes.length === 0 && <p className="text-slate-500">Aucune alerte active</p>}
          </div>
        )}

        {activeTab === 'demandes' && (
          <div className="space-y-3" data-testid="demandes-tab">
            {demandes.map((dem) => (
              <DemandeCard key={dem.id} demande={dem} expanded
                onRepondre={() => { setSelectedDemande(dem); setShowReponseModal(true); }} />
            ))}
            {demandes.length === 0 && <p className="text-slate-500">Aucune demande en attente</p>}
          </div>
        )}

        {activeTab === 'clients' && (
          <div className="space-y-3" data-testid="clients-tab">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {clients.map((client) => (
                <ClientCard key={client.id} client={client}
                  onClick={() => { setSelectedClient(client); fetchClientDetail(client.id); }} />
              ))}
            </div>
          </div>
        )}

        {activeTab === 'interventions' && (
          <div className="space-y-2" data-testid="interventions-tab">
            {interventions.map((interv) => (
              <InterventionRow key={interv.id} intervention={interv} expanded />
            ))}
          </div>
        )}

        {activeTab === 'casia' && (
          <CASIAPanel produits={dashboard?.produits_casia || {}} />
        )}

        {activeTab === 'pipeline' && (
          <PipelinePanel data={pipelineData} headers={headers} onRefresh={fetchPipeline} />
        )}

        {activeTab === 'facturation' && (
          <FacturationPanel factures={factures} headers={headers} clients={clients} onRefresh={fetchFactures} />
        )}

        {activeTab === 'diagnostic' && (
          <DiagnosticPanel diagnostics={diagnostics} />
        )}

        {activeTab === 'fiches' && (
          <FichesInterventionPanel fiches={fiches} />
        )}
      </div>

      {/* Modal detail client */}
      <Dialog open={!!selectedClient} onOpenChange={() => setSelectedClient(null)}>
        <DialogContent className="max-w-3xl bg-[#0d1321] border-slate-700 text-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-emerald-400">{selectedClient?.nom}</DialogTitle>
          </DialogHeader>
          {clientDetail && <ClientDetailView detail={clientDetail} />}
        </DialogContent>
      </Dialog>

      {/* Modal reponse demande */}
      <Dialog open={showReponseModal} onOpenChange={setShowReponseModal}>
        <DialogContent className="bg-[#0d1321] border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Repondre a {selectedDemande?.client_nom}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="p-3 rounded-lg bg-slate-800/50 text-sm text-slate-300">{selectedDemande?.message}</div>
            <textarea
              value={reponseText}
              onChange={(e) => setReponseText(e.target.value)}
              className="w-full h-32 bg-slate-900 border border-slate-700 rounded-lg p-3 text-sm text-white resize-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500"
              placeholder="Votre reponse..."
              data-testid="reponse-textarea"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowReponseModal(false)} className="border-slate-600 text-slate-300">Annuler</Button>
              <Button onClick={repondreDemande} className="bg-emerald-600 hover:bg-emerald-700" data-testid="envoyer-reponse-btn">
                <Send className="w-4 h-4 mr-1" /> Envoyer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== COMPOSANTS ====================

function StatCard({ icon: Icon, label, value, color, flash }) {
  const colorMap = {
    emerald: 'from-emerald-500/20 to-emerald-500/5 border-emerald-500/30 text-emerald-400',
    blue: 'from-blue-500/20 to-blue-500/5 border-blue-500/30 text-blue-400',
    red: 'from-red-500/20 to-red-500/5 border-red-500/30 text-red-400',
    orange: 'from-orange-500/20 to-orange-500/5 border-orange-500/30 text-orange-400',
    violet: 'from-violet-500/20 to-violet-500/5 border-violet-500/30 text-violet-400',
    amber: 'from-amber-500/20 to-amber-500/5 border-amber-500/30 text-amber-400',
  };
  return (
    <div className={`rounded-xl border bg-gradient-to-b p-3 ${colorMap[color]} ${flash ? 'animate-pulse' : ''}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4 opacity-70" />
        <span className="text-xs opacity-70">{label}</span>
      </div>
      <div className="text-2xl font-bold">{value}</div>
    </div>
  );
}

function SmartMessageCard({ message }) {
  const colorMap = {
    blue: 'border-blue-500/30 bg-blue-500/10',
    amber: 'border-amber-500/30 bg-amber-500/10',
    red: 'border-red-500/30 bg-red-500/10',
    orange: 'border-orange-500/30 bg-orange-500/10',
  };
  const iconMap = {
    thermometer: Thermometer,
    droplets: Droplets,
    bug: Bug,
    target: Target,
  };
  const Icon = iconMap[message.icone] || Zap;
  const colors = colorMap[message.couleur] || colorMap.blue;

  return (
    <div className={`rounded-xl border p-4 ${colors}`} data-testid={`smart-msg-${message.type}`}>
      <div className="flex items-start gap-3">
        <Icon className="w-5 h-5 mt-0.5 shrink-0" />
        <div className="flex-1">
          <h3 className="font-semibold text-sm">{message.titre}</h3>
          <p className="text-xs text-slate-300 mt-1">{message.message}</p>
          {message.produit_casia && (
            <Badge className="mt-2 bg-emerald-600/80 text-xs">{message.produit_casia}</Badge>
          )}
        </div>
      </div>
    </div>
  );
}

function AlerteCard({ alerte, expanded, onTraiter }) {
  const sev = SEVERITE_COLORS[alerte.severite] || SEVERITE_COLORS.basse;
  const Icon = ALERTE_ICONS[alerte.type] || AlertTriangle;

  return (
    <div className={`rounded-xl border p-3 ${sev.bg} ${sev.border} ${alerte.severite === 'critique' ? 'ring-1 ring-red-500/30' : ''}`}
      data-testid={`alerte-${alerte.id}`}>
      <div className="flex items-start gap-2">
        <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${sev.dot} ${alerte.severite === 'critique' ? 'animate-ping' : ''}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Icon className={`w-4 h-4 ${sev.text}`} />
            <span className="font-semibold text-sm truncate">{alerte.titre}</span>
            <Badge className={`${sev.badge} text-[10px] text-white`}>{alerte.severite}</Badge>
          </div>
          {alerte.client_nom && <p className="text-xs text-slate-400 mt-0.5">{alerte.client_nom}</p>}
          {expanded && (
            <>
              <p className="text-xs text-slate-300 mt-1">{alerte.message}</p>
              {alerte.action_requise && (
                <p className="text-xs text-amber-400 mt-1 font-medium">Action: {alerte.action_requise}</p>
              )}
            </>
          )}
        </div>
        <Button size="sm" variant="ghost" className={`${sev.text} hover:bg-white/10 text-xs shrink-0`} onClick={onTraiter}>
          <CheckCircle className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

function DemandeCard({ demande, expanded, onRepondre }) {
  const urgColors = {
    haute: 'border-orange-500/40 bg-orange-500/10',
    normale: 'border-slate-700 bg-slate-800/50',
  };
  const typeIcons = {
    validation: CheckCircle,
    visite: MapPin,
    photo: Eye,
    question: MessageCircle,
  };
  const Icon = typeIcons[demande.type_demande] || MessageCircle;

  return (
    <div className={`rounded-xl border p-3 ${urgColors[demande.urgence] || urgColors.normale}`}
      data-testid={`demande-${demande.id}`}>
      <div className="flex items-start gap-2">
        <Icon className="w-4 h-4 mt-0.5 text-emerald-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm">{demande.client_nom}</span>
            <Badge variant="outline" className="text-[10px] border-slate-600">{demande.type_demande}</Badge>
            {demande.urgence === 'haute' && <Badge className="bg-orange-500 text-[10px]">URGENT</Badge>}
          </div>
          <p className="text-xs text-slate-300 mt-1">{demande.demandeur}: {expanded ? demande.message : demande.message?.substring(0, 80) + '...'}</p>
          {demande.photo_url && (
            <img src={demande.photo_url} alt="Photo jointe" className="mt-2 rounded-lg max-h-32 object-cover" />
          )}
        </div>
        <Button size="sm" className="bg-emerald-600/80 hover:bg-emerald-700 text-xs shrink-0" onClick={onRepondre}>
          <Send className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

function InterventionRow({ intervention, expanded }) {
  const isNonConforme = !intervention.conforme;
  return (
    <div className={`rounded-lg border p-3 flex items-center gap-3 ${
      isNonConforme ? 'border-red-500/40 bg-red-500/10' : 'border-slate-800 bg-slate-800/30'
    }`} data-testid={`intervention-${intervention.id}`}>
      <div className={`w-2 h-2 rounded-full shrink-0 ${isNonConforme ? 'bg-red-500 animate-pulse' : 'bg-emerald-500'}`} />
      <div className="flex-1 min-w-0 grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
        <span className="font-medium text-white truncate">{intervention.client_nom}</span>
        <span className="text-slate-400 truncate">{intervention.parcelle_nom}</span>
        <span className={isNonConforme ? 'text-red-400 font-bold' : 'text-slate-300'}>{intervention.produit}</span>
        <span className="text-slate-400">{intervention.dose_ha ? `${intervention.dose_ha} L/ha` : '-'}</span>
        <span className="text-slate-500">{intervention.date?.substring(0, 10)}</span>
      </div>
      {isNonConforme && (
        <Badge className="bg-red-500 text-[10px] shrink-0 animate-pulse">NON CONFORME</Badge>
      )}
      {!isNonConforme && intervention.statut_validation === 'valide' && (
        <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
      )}
    </div>
  );
}

function ClientCard({ client, onClick }) {
  const certif = CERTIF_BADGES[client.statut_certif] || CERTIF_BADGES.conventionnel;
  const hasAlerts = (client.alertes_actives || 0) > 0;
  const score = client.conformite_score || 100;
  const scoreColor = score >= 90 ? 'text-emerald-400' : score >= 75 ? 'text-amber-400' : 'text-red-400';

  return (
    <div className={`rounded-xl border p-4 cursor-pointer transition-all hover:border-emerald-500/40 hover:bg-slate-800/50 ${
      hasAlerts ? 'border-red-500/30 bg-red-500/5' : 'border-slate-800 bg-slate-800/20'
    }`} onClick={onClick} data-testid={`client-card-${client.id}`}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <h3 className="font-bold text-sm text-white">{client.nom}</h3>
          <p className="text-xs text-slate-400">{client.dirigeant} - {client.commune}</p>
        </div>
        <Badge className={`${certif.color} text-[10px]`}>{certif.label}</Badge>
      </div>
      <div className="flex items-center gap-3 text-xs text-slate-400 mt-2">
        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{client.surface_ha} ha</span>
        <span className="flex items-center gap-1"><Leaf className="w-3 h-3" />{client.type_exploitation}</span>
        <span className={`ml-auto font-bold ${scoreColor}`}>{score}%</span>
      </div>
      {hasAlerts && (
        <div className="mt-2 flex items-center gap-1 text-xs text-red-400">
          <AlertTriangle className="w-3 h-3" /> {client.alertes_actives} alerte(s) active(s)
        </div>
      )}
    </div>
  );
}

function ClientDetailView({ detail }) {
  const { client, parcelles, interventions, alertes, demandes } = detail;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div><span className="text-slate-400">Dirigeant:</span> <span className="text-white">{client.dirigeant}</span></div>
        <div><span className="text-slate-400">Surface:</span> <span className="text-white">{client.surface_ha} ha</span></div>
        <div><span className="text-slate-400">Commune:</span> <span className="text-white">{client.commune}</span></div>
        <div><span className="text-slate-400">Certification:</span> <Badge className={CERTIF_BADGES[client.statut_certif]?.color + ' text-xs'}>{client.statut_certif}</Badge></div>
        <div><span className="text-slate-400">Email:</span> <span className="text-white text-xs">{client.contact_email}</span></div>
        <div><span className="text-slate-400">Tel:</span> <span className="text-white">{client.contact_tel}</span></div>
      </div>

      {alertes.length > 0 && (
        <div>
          <h4 className="text-xs font-bold text-red-400 uppercase mb-2">Alertes actives</h4>
          {alertes.map(a => (
            <div key={a.id} className="text-xs p-2 rounded bg-red-500/10 border border-red-500/30 mb-1">
              <span className="font-bold text-red-300">{a.titre}</span>
              <p className="text-red-200/70 mt-0.5">{a.message}</p>
            </div>
          ))}
        </div>
      )}

      {parcelles.length > 0 && (
        <div>
          <h4 className="text-xs font-bold text-emerald-400 uppercase mb-2">Parcelles ({parcelles.length})</h4>
          <div className="grid grid-cols-2 gap-2">
            {parcelles.map(p => (
              <div key={p.id} className="text-xs p-2 rounded bg-slate-800/50 border border-slate-700">
                <span className="font-medium text-white">{p.nom}</span>
                <span className="text-slate-400 ml-2">{p.surface} ha - {p.culture}</span>
                <Badge className={`ml-1 ${CERTIF_BADGES[p.statut_certif]?.color || ''} text-[9px]`}>{p.statut_certif}</Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {interventions.length > 0 && (
        <div>
          <h4 className="text-xs font-bold text-blue-400 uppercase mb-2">Dernières interventions</h4>
          {interventions.slice(0, 5).map(i => (
            <InterventionRow key={i.id} intervention={i} />
          ))}
        </div>
      )}
    </div>
  );
}

function CASIAPanel({ produits }) {
  return (
    <div className="space-y-4" data-testid="casia-panel">
      <h2 className="text-sm font-bold text-emerald-400 uppercase">Gamme Produits CASIA — Recommandations</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(produits).map(([key, prod]) => (
          <div key={key} className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
            <div className="flex items-center gap-2 mb-2">
              <FlaskConical className="w-5 h-5 text-emerald-400" />
              <h3 className="font-bold text-emerald-300">{prod.nom}</h3>
              {prod.bio_compatible && <Badge className="bg-green-600 text-[10px]">BIO</Badge>}
              {prod.hve_compatible && <Badge className="bg-emerald-700 text-[10px]">HVE</Badge>}
            </div>
            <p className="text-xs text-slate-300 mb-2">{prod.description}</p>
            <div className="text-xs text-slate-400">
              <span className="font-medium text-white">Doses:</span>
              {Object.entries(prod.doses || {}).map(([culture, dose]) => (
                <div key={culture} className="ml-2">
                  {culture}: <span className="text-emerald-400">{dose.dose_ha} {dose.unite}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ==================== CRM PIPELINE ====================
function PipelinePanel({ data, headers, onRefresh }) {
  const stages = data?.stages || [];
  const pipeline = data?.pipeline || {};

  const moveClient = async (clientId, newStage) => {
    try {
      await fetch(`${API_URL}/api/conseiller/crm/pipeline/${clientId}/move`, {
        method: 'PUT', headers: headers(),
        body: JSON.stringify({ stage: newStage })
      });
      toast.success('Client deplace');
      onRefresh();
    } catch (e) { toast.error('Erreur'); }
  };

  return (
    <div data-testid="pipeline-panel">
      <h2 className="text-sm font-bold text-emerald-400 uppercase mb-4">Pipeline CRM — Suivi Clients</h2>
      <div className="flex gap-3 overflow-x-auto pb-4">
        {stages.map(stage => {
          const col = pipeline[stage.id] || { clients: [] };
          return (
            <div key={stage.id} className="min-w-[260px] flex-shrink-0">
              <div className="flex items-center gap-2 mb-2 px-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: stage.color }} />
                <span className="text-sm font-bold text-white">{stage.label}</span>
                <Badge variant="outline" className="text-[10px] border-slate-600 ml-auto">{col.clients?.length || 0}</Badge>
              </div>
              <div className="space-y-2 bg-slate-900/50 rounded-xl border border-slate-800 p-2 min-h-[200px]">
                {(col.clients || []).map(client => (
                  <div key={client.id} className="bg-slate-800/80 rounded-lg p-3 border border-slate-700 hover:border-emerald-500/30 transition-all"
                    data-testid={`pipeline-card-${client.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold text-white truncate">{client.nom}</span>
                      <Badge className={`text-[9px] ${CERTIF_BADGES[client.statut_certif]?.color || 'bg-slate-600'}`}>
                        {client.statut_certif}
                      </Badge>
                    </div>
                    <p className="text-[11px] text-slate-400">{client.dirigeant}</p>
                    {client.total_facture > 0 && (
                      <p className="text-[11px] text-emerald-400 mt-1">
                        <Euro className="w-3 h-3 inline" /> {client.total_facture.toFixed(0)} EUR facture
                      </p>
                    )}
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {stages.filter(s => s.id !== stage.id).slice(0, 3).map(s => (
                        <button key={s.id} onClick={() => moveClient(client.id, s.id)}
                          className="text-[9px] px-2 py-0.5 rounded bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors">
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                {(!col.clients || col.clients.length === 0) && (
                  <p className="text-xs text-slate-600 text-center py-8">Aucun client</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ==================== FACTURATION ====================
function FacturationPanel({ factures: data, headers, clients, onRefresh }) {
  const [showNew, setShowNew] = useState(false);
  const [newFact, setNewFact] = useState({ client_id: '', type_facturation: 'forfait', description: '', montant_ht: 89, heures: 0, tarif_horaire: 75 });
  const facturesList = data?.factures || [];
  const stats = data?.stats || {};

  const creerFacture = async () => {
    try {
      await fetch(`${API_URL}/api/conseiller/factures`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify(newFact)
      });
      toast.success('Facture creee');
      setShowNew(false);
      onRefresh();
    } catch (e) { toast.error('Erreur'); }
  };

  const marquerPayee = async (id) => {
    await fetch(`${API_URL}/api/conseiller/factures/${id}/statut`, {
      method: 'PUT', headers: headers(),
      body: JSON.stringify({ statut: 'paye' })
    });
    toast.success('Facture marquee payee');
    onRefresh();
  };

  return (
    <div data-testid="facturation-panel">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-bold text-emerald-400 uppercase">Facturation</h2>
        <Button onClick={() => setShowNew(!showNew)} className="bg-emerald-600 hover:bg-emerald-700 text-xs" data-testid="new-facture-btn">
          <Receipt className="w-3.5 h-3.5 mr-1" /> Nouvelle facture
        </Button>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="rounded-lg bg-slate-800/50 border border-slate-700 p-3">
          <p className="text-xs text-slate-400">Total HT</p>
          <p className="text-lg font-bold text-white">{stats.total_ht?.toFixed(0) || 0} EUR</p>
        </div>
        <div className="rounded-lg bg-slate-800/50 border border-slate-700 p-3">
          <p className="text-xs text-slate-400">Factures</p>
          <p className="text-lg font-bold text-white">{stats.nb_factures || 0}</p>
        </div>
        <div className="rounded-lg bg-slate-800/50 border border-slate-700 p-3">
          <p className="text-xs text-slate-400">Payees</p>
          <p className="text-lg font-bold text-emerald-400">{stats.nb_payees || 0}</p>
        </div>
        <div className="rounded-lg bg-slate-800/50 border border-slate-700 p-3">
          <p className="text-xs text-slate-400">En attente</p>
          <p className="text-lg font-bold text-amber-400">{stats.en_attente?.toFixed(0) || 0} EUR</p>
        </div>
      </div>

      {/* New facture form */}
      {showNew && (
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 mb-4 space-y-3">
          <select value={newFact.client_id} onChange={e => setNewFact({...newFact, client_id: e.target.value})}
            className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-sm text-white" data-testid="facture-client-select">
            <option value="">Selectionner un client</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-3">
            <select value={newFact.type_facturation} onChange={e => setNewFact({...newFact, type_facturation: e.target.value})}
              className="bg-slate-900 border border-slate-700 rounded-lg p-2 text-sm text-white">
              <option value="forfait">Forfait mensuel</option>
              <option value="heure">A l'heure</option>
              <option value="mission">A la mission</option>
            </select>
            <input type="number" value={newFact.montant_ht} onChange={e => setNewFact({...newFact, montant_ht: parseFloat(e.target.value) || 0})}
              className="bg-slate-900 border border-slate-700 rounded-lg p-2 text-sm text-white" placeholder="Montant HT" />
          </div>
          <input value={newFact.description} onChange={e => setNewFact({...newFact, description: e.target.value})}
            className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-sm text-white" placeholder="Description" />
          <Button onClick={creerFacture} className="bg-emerald-600 hover:bg-emerald-700 text-xs" data-testid="submit-facture-btn">Creer la facture</Button>
        </div>
      )}

      {/* Factures list */}
      <div className="space-y-2">
        {facturesList.map(f => (
          <div key={f.id} className={`rounded-lg border p-3 flex items-center gap-3 ${
            f.statut === 'paye' ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-slate-700 bg-slate-800/30'
          }`} data-testid={`facture-${f.id}`}>
            <Receipt className={`w-5 h-5 shrink-0 ${f.statut === 'paye' ? 'text-emerald-400' : 'text-amber-400'}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm text-white">{f.numero}</span>
                <span className="text-xs text-slate-400">{f.client_nom}</span>
                <Badge className={`text-[10px] ${f.statut === 'paye' ? 'bg-emerald-600' : f.statut === 'emise' ? 'bg-amber-600' : 'bg-slate-600'}`}>
                  {f.statut}
                </Badge>
              </div>
              <p className="text-xs text-slate-400">{f.description} — {f.date?.substring(0, 10)}</p>
            </div>
            <span className="font-bold text-white text-sm">{f.montant_ttc?.toFixed(2)} EUR</span>
            {f.statut !== 'paye' && (
              <Button size="sm" variant="outline" className="border-emerald-500/40 text-emerald-400 text-xs"
                onClick={() => marquerPayee(f.id)}>Payee</Button>
            )}
          </div>
        ))}
        {facturesList.length === 0 && <p className="text-sm text-slate-500 text-center py-4">Aucune facture</p>}
      </div>
    </div>
  );
}

// ==================== DIAGNOSTIC PHOTO IA ====================
function DiagnosticPanel({ diagnostics }) {
  return (
    <div data-testid="diagnostic-panel">
      <h2 className="text-sm font-bold text-emerald-400 uppercase mb-4">Diagnostics Photo IA</h2>
      {diagnostics.length === 0 && (
        <div className="text-center py-12 text-slate-500">
          <Camera className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Aucun diagnostic photo pour le moment.</p>
          <p className="text-xs mt-1">Les clients peuvent envoyer des photos via l'agent IA pour obtenir un diagnostic automatique.</p>
        </div>
      )}
      <div className="space-y-4">
        {diagnostics.map(diag => (
          <div key={diag.id} className="rounded-xl border border-slate-700 bg-slate-800/30 overflow-hidden" data-testid={`diagnostic-${diag.id}`}>
            <div className="flex">
              {diag.photo_url && (
                <img src={diag.photo_url} alt="Photo parcelle" className="w-48 h-48 object-cover shrink-0" />
              )}
              <div className="p-4 flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-amber-600 text-[10px]">{diag.statut?.replace('_', ' ')}</Badge>
                  <span className="text-sm font-bold text-white">{diag.client_nom}</span>
                  <span className="text-xs text-slate-400">— {diag.parcelle_nom} ({diag.culture})</span>
                </div>
                {diag.description_utilisateur && (
                  <p className="text-xs text-slate-300 mb-2 italic">"{diag.description_utilisateur}"</p>
                )}
                <div className="bg-slate-900/80 rounded-lg p-3 border border-slate-700">
                  <p className="text-xs text-emerald-400 font-bold mb-1">Diagnostic IA :</p>
                  <p className="text-xs text-slate-300 whitespace-pre-line">{diag.diagnostic_ia}</p>
                </div>
                <p className="text-[10px] text-slate-500 mt-2">Demande: {diag.demandeur} — {diag.date?.substring(0, 16)}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ==================== FICHES D'INTERVENTION ====================
function FichesInterventionPanel({ fiches }) {
  return (
    <div data-testid="fiches-panel">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-bold text-emerald-400 uppercase">Fiches d'Intervention</h2>
        <Badge variant="outline" className="border-slate-600 text-slate-400 text-xs">{fiches.length} fiches</Badge>
      </div>
      <div className="space-y-3">
        {fiches.map((fiche, idx) => (
          <div key={idx} className={`rounded-xl border p-4 ${
            !fiche.conforme ? 'border-red-500/40 bg-red-500/5' : 'border-slate-700 bg-slate-800/20'
          }`} data-testid={`fiche-${fiche.numero}`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <span className="text-xs text-slate-500">Fiche #{fiche.numero}</span>
                <h3 className="font-bold text-sm text-white">{fiche.client}</h3>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-white">{fiche.date}</p>
                <p className="text-[10px] text-slate-400">{fiche.heure}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div>
                <span className="text-slate-500">Parcelle</span>
                <p className="text-white font-medium">{fiche.parcelle}</p>
              </div>
              <div>
                <span className="text-slate-500">Produit</span>
                <p className={`font-medium ${!fiche.conforme ? 'text-red-400' : 'text-white'}`}>{fiche.produit}</p>
              </div>
              <div>
                <span className="text-slate-500">Dose</span>
                <p className="text-white">{fiche.dose_ha} L/ha x {fiche.surface_traitee} ha = <span className="text-emerald-400 font-bold">{fiche.quantite_totale} L</span></p>
              </div>
              <div>
                <span className="text-slate-500">Operateur</span>
                <p className="text-white">{fiche.operateur}</p>
              </div>
            </div>
            {!fiche.conforme && (
              <div className="mt-2 flex items-center gap-1 text-xs text-red-400 font-bold">
                <ShieldAlert className="w-3.5 h-3.5" /> NON CONFORME — {fiche.validation}
              </div>
            )}
            {fiche.notes && <p className="mt-2 text-[11px] text-slate-400 italic">{fiche.notes}</p>}
          </div>
        ))}
        {fiches.length === 0 && <p className="text-sm text-slate-500 text-center py-4">Aucune fiche d'intervention</p>}
      </div>
    </div>
  );
}
